import { useEffect, useState } from 'react'

interface PageInfo {
  hasNextPage: boolean
  endCursor: string | null
}

interface Props<T> {
  query: (variables: { afterCursor: string | null }) => Promise<{ data: Array<T>; pageInfo: PageInfo }>
  initialData: Array<T>
  initialPageInfo: PageInfo
}

interface Result<T> {
  data: Array<T>
  pageInfo: PageInfo
  isLoading: boolean
  handleLoadMore: () => void
}

function usePaginatedData<T>({ initialData, initialPageInfo, query }: Props<T>): Result<T> {
  const [data, setData] = useState(initialData)
  const [pageInfo, setPageInfo] = useState(initialPageInfo)
  const [isLoading, setIsLoading] = useState(false)

  const handleLoadMore = () => {
    setIsLoading(true)
    void query({ afterCursor: pageInfo.endCursor }).then(({ data, pageInfo }) => {
      setData((prevData) => [...prevData, ...data])
      setPageInfo(pageInfo)
      setIsLoading(false)
    })
  }

  // For SSR - When new data comes in, and we reset our local state.
  // Especially important if we've already "loaded more" data - definitely needs to be reset then
  useEffect(() => {
    setData(initialData)
    setPageInfo(initialPageInfo)
    setIsLoading(false)
  }, [initialData, initialPageInfo])

  return {
    data,
    pageInfo,
    isLoading,
    handleLoadMore,
  }
}

export default usePaginatedData
